import React from 'react';
import { Shield, FileText, Clock, Wifi, AlertTriangle, CheckCircle } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Terms & <span className="text-cyan-400">Conditions</span>
          </h1>
          <p className="text-xl text-gray-300">
            HVGTEC Internet Service Provider Agreement
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <p className="text-gray-300 leading-relaxed">
              By subscribing to HVGTEC's fiber-to-the-home (FTTH) internet services, you agree to the following terms and conditions. Please read carefully before submitting your application.
            </p>
          </div>

          {/* Lock-in Periods */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="text-cyan-400" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Lock-in Periods & Contracts</h2>
                <p className="text-gray-400">Subscription commitment requirements</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Premium Plans (₱999 - ₱1,599/month)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span><strong>24-month lock-in period</strong> is required for free installation benefit</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Early termination fee: Remaining months × 50% of monthly subscription</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Contract automatically becomes month-to-month after 24 months</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Economy Plans (₱599 - ₱899/month)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span><strong>No lock-in period</strong> - month-to-month subscription</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Installation fee (₱1,500) spread over 6 months (₱250/month)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Early disconnection within 6 months: Remaining installation balance becomes due</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Equipment Ownership */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Wifi className="text-cyan-400" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Equipment Ownership & Installation</h2>
                <p className="text-gray-400">What you own vs. what HVGTEC owns</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">HVGTEC Equipment (Remains HVGTEC Property)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">•</span>
                    <span><strong>Optical Network Unit (ONU)</strong> - Fiber termination device</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">•</span>
                    <span><strong>Wi-Fi Router</strong> - Provided on loan for the subscription duration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">•</span>
                    <span><strong>Fiber Optic Cable</strong> - From distribution point to premises</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">•</span>
                    <span className="text-sm text-gray-400 italic mt-2">All equipment must be returned upon service termination. Unreturned or damaged equipment will incur replacement charges (ONU: ₱3,500, Router: ₱2,000)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">What Installation Covers</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Labor costs for fiber installation and testing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Transportation of equipment and materials to installation site</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Standard mounting and cable management (up to 100 meters from distribution point)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Initial configuration and speed testing</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Fair Usage Policy */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="text-cyan-400" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Fair Usage Policy (FUP)</h2>
                <p className="text-gray-400">Network integrity and acceptable use guidelines</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">"Unlimited" Data Definition</h3>
                <p className="text-gray-300 mb-4">
                  All HVGTEC plans advertise unlimited data for residential use. However, to ensure network quality for all subscribers and protect our OSPF/BGP/VPLS backbone infrastructure, the following guidelines apply:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Unlimited data is for <strong>personal, residential use only</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>No data caps on streaming, browsing, gaming, video calls, or downloads</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Speed throttling does not apply under normal residential usage</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <AlertTriangle className="text-yellow-400 mr-2" size={20} />
                  Prohibited Activities
                </h3>
                <p className="text-gray-300 mb-3">The following activities may result in service suspension or termination:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span><strong>Commercial reselling</strong> of bandwidth without authorization</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span><strong>Running public servers</strong> (file sharing, game servers, VPN endpoints) on residential plans</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span><strong>Excessive bandwidth abuse</strong> (consistently maxing out connection 24/7 with automated downloads/uploads)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span><strong>Network attacks</strong> including DDoS, port scanning, or any malicious traffic</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span><strong>Copyright infringement</strong> or illegal content distribution</span>
                  </li>
                </ul>
                <p className="text-sm text-yellow-400 mt-4 italic">
                  💡 For business/commercial use cases, please inquire about our dedicated business plans with static IPs and SLA guarantees.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Network Management</h3>
                <p className="text-gray-300">
                  HVGTEC reserves the right to implement traffic management measures during network congestion to ensure fair access for all users. We monitor network health using industry-standard protocols and will contact subscribers if unusual patterns are detected before taking any action.
                </p>
              </div>
            </div>
          </div>

          {/* Service Level & Support */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="text-cyan-400" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Service Level & Technical Support</h2>
                <p className="text-gray-400">What you can expect from HVGTEC</p>
              </div>
            </div>

            <div className="space-y-4 text-gray-300">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Uptime Guarantee</h3>
                <p className="mb-3">
                  HVGTEC targets <strong className="text-cyan-400">99.9% network uptime</strong> on our carrier-grade infrastructure. Planned maintenance will be announced 48 hours in advance.
                </p>
                <p className="text-sm text-gray-400">
                  Note: Uptime excludes force majeure events (typhoons, earthquakes, floods) and third-party fiber cuts beyond HVGTEC's control.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Technical Support</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span><strong>24/7 hotline support</strong> for Premium plan subscribers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span><strong>Standard support hours</strong> (8AM-6PM) for Economy plans, with emergency hotline available</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>Target response time: 4 hours for service outages, 24 hours for technical issues</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span>WhatsApp and Telegram support channels available</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Payment Terms</h2>
            <div className="space-y-4 text-gray-300">
              <div className="flex items-start">
                <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <span>Monthly billing cycle due on the subscription start date each month</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <span>5-day grace period for payment; service suspended on the 6th day of non-payment</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <span>Reconnection fee: ₱200 after suspension</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <span>Account closure after 30 days of non-payment</span>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Questions About These Terms?</h2>
            <p className="text-gray-300 mb-6">
              Our team is happy to clarify any part of our Terms & Conditions before you subscribe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl transition-colors text-center">
                Contact Support
              </a>
              <a href="tel:+631234567890" className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors text-center">
                Call +63 123 456 7890
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
